import React, { useState, useEffect } from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import { getActiveEvent } from '../lib/cms';

export default function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const [eventData, setEventData] = useState(null);
  const location = useLocation();

  useEffect(() => {
    getActiveEvent().then(setEventData);
  }, []);

  // Luk mobilmenu ved ruteskift
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  const billetlink = eventData?.billetlink || "https://billet.eventbilletten.dk/seller/foreningen-rbike-zd1k";
  const billetstatus = eventData?.billetstatus || "Køb billet";

  const navLinks = [
    { to: "/eventet", label: "Eventet" },
    { to: "/billetter", label: "Billetter" },
    { to: "/godt-formaal", label: "Godt formål" },
    { to: "/partnere", label: "Partnere" },
    { to: "/om-os", label: "Om os" },
    { to: "/orbike-365", label: "ØrBike 365" },
    { to: "/praktisk-info", label: "Praktisk info" },
    { to: "/regnskaber", label: "Regnskab" },
  ];

  return (
    <header className="absolute top-0 left-0 right-0 z-50 bg-transparent border-b border-pink-500/20 transition-all duration-300">
      <div className="max-w-[1520px] mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3 group">
          <img 
            src="https://assets.auxodev.dk/assets/c6ea7980-b639-49a1-80cc-f044c1665109/orbike-logo-1.png" 
            alt="Foreningen ØrBike Logo" 
            className="h-12 sm:h-14 md:h-16 w-auto object-contain transition-transform duration-300 group-hover:scale-105"
          />
        </Link>

        {/* Desktop Navigation (Vises kun ved >= 1200px) */}
        <nav className="hidden min-[1200px]:flex items-center gap-3.5 xl:gap-5 min-[1400px]:gap-6">
          {navLinks.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              className={({ isActive }) =>
                `text-[13px] xl:text-sm font-semibold tracking-wider uppercase transition-all duration-200 py-1 border-b-2 whitespace-nowrap ${
                  isActive
                    ? "text-pink-400 border-pink-500 shadow-[0_2px_10px_rgba(255,42,141,0.5)]"
                    : "text-slate-300 border-transparent hover:text-white hover:border-pink-500/50"
                }`
              }
            >
              {link.label}
            </NavLink>
          ))}
        </nav>

        {/* Action button - Køb billet (Desktop) */}
        <div className="hidden min-[1200px]:flex items-center gap-4">
          <a
            href={billetlink}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-gradient-to-r from-pink-500 via-purple-600 to-pink-500 bg-[length:200%_auto] hover:bg-[position:right_center] text-white font-bold text-sm uppercase tracking-wider shadow-[0_0_20px_rgba(255,42,141,0.4)] hover:shadow-[0_0_25px_rgba(255,42,141,0.7)] transition-all duration-300 hover:scale-105"
          >
            <i className="fa-solid fa-ticket" aria-hidden="true"></i>
            <span>{billetstatus}</span>
          </a>
        </div>

        {/* Mobil / Tablet: Hurtig Billet-knap + Burger toggle (< 1200px) */}
        <div className="flex min-[1200px]:hidden items-center gap-3">
          <a
            href={billetlink}
            target="_blank"
            rel="noopener noreferrer"
            className="px-4 py-2 rounded-full bg-gradient-to-r from-pink-500 to-purple-600 text-white font-bold text-xs uppercase tracking-wider shadow-[0_0_15px_rgba(255,42,141,0.4)] transition-transform active:scale-95"
          >
            <i className="fa-solid fa-ticket mr-1.5" aria-hidden="true"></i>
            <span>Billet</span>
          </a>

          <button
            type="button"
            onClick={() => setIsOpen(!isOpen)}
            className="p-2.5 rounded-lg text-slate-300 hover:text-white hover:bg-slate-800/80 focus:outline-none focus:ring-2 focus:ring-pink-500 transition-colors"
            aria-label="Åbn menu"
          >
            {isOpen ? (
              <i className="fa-solid fa-xmark text-xl text-pink-400" aria-hidden="true"></i>
            ) : (
              <i className="fa-solid fa-bars text-xl" aria-hidden="true"></i>
            )}
          </button>
        </div>
      </div>

      {/* Mobil Dropdown Menu (< 1200px) */}
      {isOpen && (
        <div className="min-[1200px]:hidden bg-[#0e1017] border-b border-pink-500/30 px-4 pt-3 pb-6 space-y-3 animate-in fade-in slide-in-from-top-4 duration-200">
          <nav className="flex flex-col space-y-2">
            {navLinks.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                onClick={() => setIsOpen(false)}
                className={({ isActive }) =>
                  `px-4 py-3 rounded-lg text-base font-semibold transition-all ${
                    isActive
                      ? "bg-pink-500/10 text-pink-400 border-l-4 border-pink-500 font-bold"
                      : "text-slate-300 hover:bg-slate-800/50 hover:text-white"
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
            <Link
              to="/fotoarkiv"
              onClick={() => setIsOpen(false)}
              className="px-4 py-3 rounded-lg text-base font-medium text-slate-400 hover:text-white hover:bg-slate-800/50 transition-all"
            >
              Fotoarkiv
            </Link>
            <Link
              to="/kontakt"
              onClick={() => setIsOpen(false)}
              className="px-4 py-3 rounded-lg text-base font-medium text-slate-400 hover:text-white hover:bg-slate-800/50 transition-all"
            >
              Kontakt
            </Link>
          </nav>

          <div className="pt-3 border-t border-slate-800">
            <a
              href={billetlink}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-gradient-to-r from-pink-500 via-purple-600 to-pink-500 text-white font-bold text-base uppercase tracking-wider shadow-[0_0_20px_rgba(255,42,141,0.5)] transition-transform active:scale-95"
            >
              <i className="fa-solid fa-ticket text-lg" aria-hidden="true"></i>
              <span>{billetstatus} på Eventbilletten</span>
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
